import { CSSProperties } from 'react';
import {
	UNSTABLE_ToastRegion as ToastRegion,
	UNSTABLE_Toast as Toast,
	UNSTABLE_ToastQueue as ToastQueue,
	UNSTABLE_ToastContent as ToastContent,
	ToastProps,
	Button,
} from 'react-aria-components';
import { flushSync } from 'react-dom';
import cx from 'classix';
import { Icon } from '../atoms';

// Lifted almost completely from https://react-aria.adobe.com/Toast

// Define the type for your toast content. This interface defines the properties of your toast content, affecting what you
// pass to the queue calls as arguments.
type MWToastProps = {
	description: string;
	timeout?: number;
}

// This is a global toast queue, to be imported and called where ever you want to queue a toast via toastQueue.add().
const toastQueue = new ToastQueue<MWToastProps>({
	// Wrap state updates in a CSS view transition.
	wrapUpdate(fn) {
		if ('startViewTransition' in document) {
			document.startViewTransition(() => {
				flushSync(fn);
			});
		} else {
			fn();
		}
	}
});

/** Very basic implement of react-aria-component Toast. Optional timeout to self dismiss with description. Currently only supports success toasts. */
export const addToast = (props: MWToastProps) => {
	toastQueue.add({ description: props.description }, { timeout: props.timeout ?? 5000 });
}

export function MyToastRegion() {
	return (
		// The ToastRegion should be rendered at the root of app.
		<ToastRegion
			queue={toastQueue}
			className="fixed top-22 right-8 m-1 flex gap-2 bg-success-25 rounded-lg border-success-100 border text-success-500 shadow-md">
			{({ toast }) => (
				<>
					<MyToast toast={toast}>
						<ToastContent className={cx("min-w-0")}>
							{toast.content.description && (
								<div className='flex gap-1 items-center p-4'>
									<Icon type='check-circle' size='24' />
									<p slot="description" className="text-sm">{toast.content.description}</p>
								</div>
							)}
						</ToastContent>
						{/* Using react-aria vs design system button to take advantage of SlotProps. Additionally, our current IconButton is a mess */}
						<Button slot='close' aria-label='close' className='self-start p-2'>
							<Icon type='x' size='18' />
						</Button>
					</MyToast>
				</>
			)}
		</ToastRegion>
	);
}

export function MyToast(props: ToastProps<MWToastProps>) {
	return (
		<Toast
			{...props}
			style={{ viewTransitionName: props.toast.key } as CSSProperties}
			className={cx(
				props.className as string,
				"[view-transition-class:toast] max-w-[500px] flex gap-2"
			)}
		/>
	);
}
